'use client';

import React, { useMemo, useDeferredValue } from 'react';
// eslint-disable-next-line import/no-unresolved
import { useInfiniteHits } from 'react-instantsearch-next';
import useInfiniteStaticProducts from 'hooks/useInfiniteStaticProducts';
import useProductVariantsToShow from 'hooks/useProductListingData/useProductVariantsToShow';
import AdvancedSearchProductsGrid from '../AdvancedSearchProductsGrid';
import LoadMoreButton from '../AdvancedSearchProductsGrid/LoadMoreButton';

const AdvancedSearchProductHits = () => {
  const { items, showMore, isLastPage } = useInfiniteHits();

  const productIdsRaw = useMemo(() => {
    if (!items?.length) return [];
    return items
      .map(hit => {
        const id = hit.product_id;
        return typeof id === 'string' ? parseInt(id, 10) : id;
      })
      .filter(id => id > 0 && !Number.isNaN(id));
  }, [items]);

  const productIds = useDeferredValue(productIdsRaw);

  const { products, loading } = useInfiniteStaticProducts(
    {
      productIds,
      fetchLimit: productIds?.length,
    },
    {
      skipSorting: true,
    }
  );

  // Use the same variant selection logic as the old search page
  const productsWithVariantsToShow = useProductVariantsToShow({
    colorFilters: null,
    products,
    upholsteryLabelFilters: null,
  });

  const productsInAlgoliaOrder = useMemo(() => {
    if (!items?.length || !productsWithVariantsToShow?.length) {
      return productsWithVariantsToShow ?? [];
    }

    const productsByIdMap = new Map();
    productsWithVariantsToShow.forEach(product => {
      const idNormalized = String(product.id);
      if (!productsByIdMap.has(idNormalized)) {
        productsByIdMap.set(idNormalized, product);
      }
    });

    return items
      .map(
        ({
          __position,
          __queryID,
          is_in_stock,
          objectID,
          product_id: productIdFromAlgolia,
        }) => {
          const productIdNormalized = String(productIdFromAlgolia);
          const productIdNumber = parseInt(productIdFromAlgolia, 10);
          const product = productsByIdMap.get(productIdNormalized);

          if (!product) {
            return null;
          }

          const isInStock = is_in_stock === true;

          return {
            ...product,
            quickship: isInStock,
            variants: (product.variants || []).map(variantData => ({
              ...variantData,
              quickship: isInStock,
            })),
            variantsToShow: (product.variantsToShow || []).map(variantData => ({
              ...variantData,
              quickship: isInStock,
            })),
            algoliaMetaData: {
              queryID: __queryID ?? '',
              position: __position ?? 0,
              objectID: objectID ?? productIdNumber ?? '',
            },
          };
        }
      )
      .filter(Boolean);
  }, [items, productsWithVariantsToShow]);

  if (!items?.length) {
    return <div>No results found.</div>;
  }

  return (
    <div>
      {productsInAlgoliaOrder?.length > 0 && (
        <AdvancedSearchProductsGrid products={productsInAlgoliaOrder} />
      )}
      {productsInAlgoliaOrder?.length > 0 && (
        <div className="flex justify-center mt-8 mb-12">
          <LoadMoreButton
            onClick={showMore}
            disabled={isLastPage}
            isLoading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default AdvancedSearchProductHits;
